// Champion panel — shown inside the unit tab when the selected unit carries
// the `legend` prop (so: only while legends.enabled is on). Generic over the
// kind (dragon / golem / lich): HP, level, kills, status badges, the Lich's
// Soul meter, a Golem's stance line and one control per ability from
// `legendRows` (ChampionAbilities). Casting a targeted ability is two clicks:
// the button arms targeting, the next hex click casts (castAbility).

import type { SceneNode } from '@aod/protocol';
import { Progress } from '@aod/ui/components/ui/progress';
import { SceneImage } from '@aod/ui/components/ui/scene';
import { Crown } from 'lucide-react';
import { useEffect } from 'react';
import { type MessageKey, useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import { bossSceneId, sceneUrls } from '../ui/scenes.js';
import { ChampionAbilities } from './ChampionAbilities.js';
import { useChampion } from './ChampionContext.js';
import { UnitStatusBadges } from './ChampionStatusBadges.js';
import { abilityViews, hasSoul, stanceOf, unitSoul } from './championKinds.js';
import { killsForLevel, phylacteryCityOf, unitLegend } from './championRules.js';
import { useChampionUi } from './championStore.js';

export function ChampionPanel({ unit }: { unit: SceneNode }) {
  const t = useT();
  const api = useChampion();
  const legend = unitLegend(unit);
  const targeting = useChampionUi((s) => s.targeting);
  const error = useChampionUi((s) => s.lastError);
  const errorKey = useChampionUi((s) => s.lastErrorKey);
  const notice = useChampionUi((s) => s.lastNotice);
  const unitId = unit.id;

  // Esc cancels targeting; leaving the panel (selection change) does too.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') useChampionUi.getState().cancelTargeting();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const cur = useChampionUi.getState();
      if (cur.targeting?.unitId === unitId) cur.cancelTargeting();
      cur.setError(null);
      cur.setNotice(null);
    };
  }, [unitId]);

  if (!legend || !api) return null;
  const rows = api.rows;

  const ownerId = (unit.props?.ownerId as number | undefined) ?? 0;
  const own = ownerId === api.localPlayerIndex;
  const hp = (unit.props?.hp as number | undefined) ?? 0;
  const hpMax = (unit.props?.hpMax as number | undefined) ?? 0;
  const hpPct = hpMax > 0 ? Math.round((hp / hpMax) * 100) : 0;
  const kills = own ? api.legend.kills : null;
  const nextKills = killsForLevel(rows, legend.level + 1);
  const kindKey = `champion.kind.${legend.kind}` as MessageKey;
  const boss = bossSceneId(legend.kind);
  const views = abilityViews(rows, legend.kind, unit, legend.cooldowns);
  const soul = unitSoul(unit);
  const stance = stanceOf(unit);
  const armedView = views.find((v) => targeting?.unitId === unitId && targeting.slot === v.slot);
  const phylacheryId = own ? phylacteryCityOf(api.legend) : null;
  const phylacheryCity = phylacheryId
    ? api.snapshot?.scene.children?.find((n) => n.id === phylacheryId)
    : undefined;

  return (
    <section
      data-testid="champion-panel"
      {...appDom({
        id: 'champion-panel',
        type: 'Card',
        scope: 'aside',
        label: t('champion.title'),
      })}
      data-kind={legend.kind}
      className="flex flex-col gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2"
    >
      {boss && (
        <SceneImage
          {...sceneUrls('boss', boss)}
          widths={[320, 640]}
          sizes="280px"
          aspect="wide"
          focus="top"
          alt={t(kindKey)}
          data-testid="champion-art"
        />
      )}
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-semibold">
          <Crown className="size-3.5 text-amber-500" />
          {own ? t('champion.title') : t('champion.rival')}
          <span className="font-normal text-muted-foreground">{t(kindKey)}</span>
        </span>
        <span
          data-testid="champion-level"
          {...appDom({ id: 'champion-level', type: 'Badge', scope: 'aside' })}
          className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums"
        >
          {t('champion.level', { level: legend.level, max: rows.maxLevel })}
        </span>
      </div>

      <div className="flex flex-col gap-0.5">
        <div className="flex justify-between text-[10px] text-muted-foreground">
          <span>{t('champion.hp')}</span>
          <span data-testid="champion-hp" className="tabular-nums">
            {hp} / {hpMax}
          </span>
        </div>
        <Progress value={hpPct} aria-label={t('champion.hp')} />
      </div>

      {kills !== null && (
        <div data-testid="champion-kills" className="text-[10px] text-muted-foreground">
          {t('champion.kills', { kills })}
          {' · '}
          {nextKills !== null
            ? t('champion.nextLevel', { kills: nextKills })
            : t('champion.maxLevel')}
        </div>
      )}

      <UnitStatusBadges unit={unit} tick={api.tick} />

      {stance && (
        <p
          data-testid="champion-stance"
          data-stance={stance}
          className="text-[10px] text-stone-700 dark:text-stone-300"
        >
          {t(`champion.state.${stance}` as MessageKey)}
        </p>
      )}

      {hasSoul(rows, legend.kind) && rows.soul && (
        <div
          className="flex flex-col gap-0.5"
          title={t('champion.soul.hint', { range: rows.soul.range })}
        >
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>{t('champion.soul.label')}</span>
            <span data-testid="champion-soul-value" className="tabular-nums">
              {soul >= rows.soul.max
                ? t('champion.soul.ready')
                : t('champion.soul.value', { soul, max: rows.soul.max })}
            </span>
          </div>
          <Progress
            value={Math.round((soul / Math.max(1, rows.soul.max)) * 100)}
            aria-label={t('champion.soul.label')}
            data-testid="champion-soul"
          />
        </div>
      )}

      {phylacheryId && (
        <p data-testid="champion-phylactery" className="text-[10px] text-muted-foreground">
          {t('champion.phylactery.panel', {
            city:
              (phylacheryCity?.props?.name as string | undefined) ??
              t('champion.phylactery.fallbackCity'),
          })}
        </p>
      )}

      {own && (
        <div className="flex flex-col gap-1">
          <ChampionAbilities unit={unit} api={api} views={views} soul={soul} />
          {armedView && (
            <p
              data-testid="champion-targeting-hint"
              className="text-[10px] text-amber-700 dark:text-amber-400"
            >
              {t('champion.ability.targetingRange', { range: armedView.ability.range })}
            </p>
          )}
          {notice && (
            <p data-testid="champion-notice" className="text-[10px] text-muted-foreground">
              {t(`champion.hint.${notice}` as MessageKey)}
            </p>
          )}
          {error && (
            <p data-testid="champion-error" className="text-[10px] text-red-700 dark:text-red-400">
              {t(errorText(error, errorKey), {})}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

const KNOWN_ERRORS = new Set([
  'on_cooldown',
  'out_of_range',
  'not_owner',
  'not_a_legend',
  'no_such_slot',
  'module_off',
  'unavailable',
  'condition_unmet',
  'fortified',
]);

/** i18n key of a rejection; `condition_unmet` has a per-ability message (Bulwark / Ascend). */
export function errorText(code: string, abilityKey: string | null): MessageKey {
  if (!KNOWN_ERRORS.has(code)) return 'champion.err.rejected';
  const specific = `champion.err.${code}.${abilityKey}`;
  if (code === 'condition_unmet' && (abilityKey === 'bulwark' || abilityKey === 'ascend')) {
    return specific as MessageKey;
  }
  return `champion.err.${code}` as MessageKey;
}
